//#region imports
import React, { useEffect, useState } from 'react';

// tremor components
import { Toggle, ToggleItem, DateRangePicker } from '@tremor/react';

// atoms
import { Toggle as CustomToggle, SearchBar } from '_atoms';

// redux
import { useSelector, useDispatch } from 'react-redux';
import {
    selectSelectedDepartureArrival,
    setSelectedDepartureArrival,
    setIncludeNoShows,
    selectIncludeNoShows,
    selectSelectedReportType,
    selectIncludeAssistanceBreakdown,
    setIncludeAssistanceBreakdown,
    selectShowPaxJourney,
    setShowPaxJourney,
    selectHideNotServed,
    setHideNotServed,
    selectShowOnlyNotServed,
    setShowOnlyNotServed,
    selectPassengerRecordsReport,
    selectPassengerRecordsReportStatus,
    setSelectedAirline,
    selectSelectedAirline
} from '_redux/slices/timeline-reports/timeline-reports';
import { selectIata } from '_redux/slices/main-screen/user';
import Select from "react-select";

// styling
import { Spacing } from '_styles';
import { airlines } from '_utils/total-reports-constants';
import { getIataTimeZone } from '_utils/iata';
import moment from 'moment';
//#endregion

const TimelineReportDashboard = (props) => {

    // redux
    const dispatch = useDispatch();

    // selected report type
    const selectedReportType = useSelector(selectSelectedReportType) || 'passenger-records';;

    // toggle value for departures/arrivals/all
    const selectedDepartureArrival = useSelector(selectSelectedDepartureArrival);

    // include no shows toggle from state
    const includeNoShows = useSelector(selectIncludeNoShows);

    // include assistance breakdowns
    const includeAssistanceBreakdown = useSelector(selectIncludeAssistanceBreakdown);
    const showPaxJourney = useSelector(selectShowPaxJourney);
    const hideNotServed = useSelector(selectHideNotServed);
    const showOnlyNotServed = useSelector(selectShowOnlyNotServed);
    const passengerRecordsReport = useSelector(selectPassengerRecordsReport);
    const reportStatus = useSelector(selectPassengerRecordsReportStatus);

    // count unverified tasks, they should have pickUpLocation and no verification method
    const unverifiedCount = React.useMemo(() => {
        const bookings = passengerRecordsReport?.processedBookings;
        if (!Array.isArray(bookings)) return 0;
        let count = 0;
        bookings.forEach(b => {
            if (!Array.isArray(b.totalJourney)) return;
            b.totalJourney.forEach(j => {
                if (!j?.taskId || !j?.pickUpLocation?.id) return;
                const info = j.atLocationInfo;
                if (!info || !(info.beaconVerified || info.qrVerified || info.geofenceVerified || info.type)) count++;
            });
        });
        return count;
    }, [passengerRecordsReport]);

    const selectedAirline = useSelector(selectSelectedAirline);
    const iata = useSelector(selectIata);
    const airportTz = getIataTimeZone(iata);
    // Handle departure/arrival change
    const handleDepartureArrivalChange = (value) => {
        dispatch(setSelectedDepartureArrival(value));
    };

    const handleExportCSV = () => {
        const bookings = passengerRecordsReport?.processedBookings;
        if (!Array.isArray(bookings) || bookings.length === 0) return;

        const filtered = hideNotServed
            ? bookings.filter(b => b.allocated !== 'Not Served')
            : showOnlyNotServed
                ? bookings.filter(b => b.allocated === 'Not Served')
                : bookings;

        const isDepartures = selectedDepartureArrival === 'Departures';
        const isArrivals = selectedDepartureArrival === 'Arrivals';
        const timeHeaders = isDepartures ? ['STD', 'ATD'] : isArrivals ? ['STA', 'ATA'] : ['STD', 'ATD', 'STA', 'ATA'];
        const headers = [
            'Name', 'Assistance', 'Flight Number', 'Pre-Advised', 'Airport',
            ...timeHeaders,
            'Created',
            'Journey Type', 'Check-in', 'Allocated Time', 'Allocated By', 'Pick Up', 'Drop Off', 'System Gate',
            'Confirm Time', 'At Loc Time', 'At Loc Verified By', 'At Loc Source',
            'Start Time', 'Complete Time', 'Agent', 'Comments', 'Vehicle', 'Status',
        ];

        const escapeCSV = (v) => {
            if (v === null || v === undefined) return '';
            const s = String(v);
            if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };

        const timeFormatOpts = {
            hour: '2-digit',
            minute: '2-digit',
            ...(airportTz ? { timeZone: airportTz } : {}),
        };

        const formatTime = (val) => {
            if (!val) return '';
            if (typeof val === 'object' && ('_seconds' in val || 'seconds' in val)) {
                const secs = val._seconds ?? val.seconds;
                const ms = (val._nanoseconds ?? val.nanoseconds ?? 0) / 1000000;
                return new Date(secs * 1000 + ms).toLocaleTimeString('en-GB', timeFormatOpts);
            }
            const d = new Date(val);
            if (isNaN(d.getTime())) return String(val);
            return d.toLocaleTimeString('en-GB', timeFormatOpts);
        };

        const getVerifiedBy = (info) => {
            if (!info) return '';
            if (info.beaconVerified) return 'Beacon';
            if (info.qrVerified) return 'QR';
            if (info.geofenceVerified) return 'Geofence';
            if (info.type) return info.type;
            return '';
        };

        const getStatus = (leg) => {
            if (leg?.taskCancelled) return 'Rejected';
            if (leg?.journey?.startsWith?.('transferred')) return 'Transferred';
            if (leg?.completeTime) return 'Completed';
            return 'Active';
        };

        const rows = [];
        filtered.forEach(b => {
            const baseFields = [
                b.name || '',
                b.assistance || '',
                b.flightNumber || '',
                b.preAdvised ? 'Yes' : 'No',
                b.airport || '',
                ...(isDepartures
                    ? [formatTime(b.std), formatTime(b.atd)]
                    : isArrivals
                        ? [formatTime(b.sta), formatTime(b.ata)]
                        : [formatTime(b.std), formatTime(b.atd), formatTime(b.sta), formatTime(b.ata)]),
                formatTime(b.dateCreated),
            ];

            const journeyLegs = Array.isArray(b.totalJourney) ? b.totalJourney : [];

            if (journeyLegs.length === 0) {
                rows.push([
                    ...baseFields,
                    '', '', '', '', '', '', '', '', '', '', '', '', '', '',
                    b.agent || '',
                    b.comments || '',
                    '', '',
                ]);
                return;
            }

            journeyLegs.forEach(leg => {
                rows.push([
                    ...baseFields,
                    leg.journey || '',
                    formatTime(leg.checkInTime),
                    formatTime(leg.allocatedTime),
                    leg.assignedBy?.name || '',
                    leg.pickUpLocation?.name || '',
                    leg.dropOffLocation?.name || '',
                    leg.allocatedDropOffLocation?.name || '',
                    formatTime(leg.acknowledgedTime),
                    formatTime(leg.atLocationTime),
                    getVerifiedBy(leg.atLocationInfo),
                    leg.atLocationInfo?.source || '',
                    formatTime(leg.startTime),
                    formatTime(leg.completeTime),
                    leg.employeeName || '',
                    '',
                    leg.startedWithEquipment || '',
                    getStatus(leg),
                ]);
            });
        });

        const csv = [headers, ...rows].map(r => r.map(escapeCSV).join(',')).join('\r\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const startDate = moment(new Date(props.dateRange[0])).format().split("T")[0];
        const endDate = moment(new Date(props.dateRange[1] ? props.dateRange[1] : props.dateRange[0])).format().split("T")[0];
        const filedate = props.singleDateOnly ? startDate : `${startDate}_${endDate}`;
        link.setAttribute('download', `allocation-records_${filedate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    const datePickerNode = props.singleDateOnly ? (
        <input
            type="date"
            value={props.dateRange?.[0] ? new Date(props.dateRange[0]).toISOString().split('T')[0] : ''}
            onChange={(e) => {
                const val = e.target.value;
                if (val) {
                    const d = new Date(val + 'T12:00:00');
                    props.setDateRange([d, d]);
                }
            }}
            style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '14px',
                minWidth: '160px',
            }}
        />
    ) : (
        <DateRangePicker
            defaultValue={props.dateRange}
            disabled={false}
            onValueChange={(value) => { props.setDateRange(value) }}
            value={props.dateRange}
            enableDropdown={true}
            maxWidth="max-w-lg"
            minWidth="max-w-lg"
            dropdownPlaceholder={'Today'}
        />
    );

    return (
        <div style={{ marginTop: '24px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>

            {/* allocation records */}
            {selectedReportType === 'passenger-records' && (
                <>
                    {/* row 1: dep/arr toggle + airline filter + search bar (grows) + date picker */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <Toggle
                            color="zinc"
                            defaultValue={selectedDepartureArrival}
                            onValueChange={handleDepartureArrivalChange}
                        >
                            <ToggleItem value="Departures" text="Departures" />
                            <ToggleItem value="Arrivals" text="Arrivals" />
                            <ToggleItem value="All" text="All" />
                        </Toggle>
                        <div style={{ width: '200px' }}>
                            <Select
                                name="Airlines"
                                options={airlines}
                                className="basic-select"
                                classNamePrefix="select"
                                onChange={s => dispatch(setSelectedAirline(s.value))}
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                value={
                                    selectedAirline
                                        ? (airlines.find(airline => airline.value === selectedAirline) || null)
                                        : null
                                }
                                placeholder="Airline Filter"
                            />
                        </div>
                        <div style={{ width: '500px' }}>
                            <SearchBar
                                value={props.searchQuery ?? ''}
                                onChange={props.onSearchChange}
                                placeholder="Search by passenger name or flight number"
                                style={{ flex: 1, minWidth: '200px', maxWidth: 'none' }}
                            />
                        </div>
                        <div style={{ marginLeft: 'auto', flexShrink: 0 }}>{datePickerNode}</div>
                    </div>

                    {/* row 2: toggles + export */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <CustomToggle
                            label="Display Passenger Journey"
                            checked={showPaxJourney}
                            onChange={(e) => dispatch(setShowPaxJourney(e.target.checked))}
                            variant="primary"
                            size="default"
                        />
                        <CustomToggle
                            label="Assisted Only"
                            checked={hideNotServed}
                            onChange={(e) => dispatch(setHideNotServed(e.target.checked))}
                            variant="primary"
                            size="default"
                        />
                        <CustomToggle
                            label="Unassisted Only"
                            checked={showOnlyNotServed}
                            onChange={(e) => dispatch(setShowOnlyNotServed(e.target.checked))}
                            variant="primary"
                            size="default"
                        />
                        <button
                            onClick={handleExportCSV}
                            disabled={!passengerRecordsReport?.processedBookings?.length}
                            style={{
                                padding: '6px 14px',
                                fontSize: '13px',
                                fontWeight: 500,
                                borderRadius: '6px',
                                border: '1px solid #d1d5db',
                                backgroundColor: '#fff',
                                color: '#374151',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Export CSV
                        </button>
                    </div>
                </>
            )}

            {/* passenger breakdown */}
            {selectedReportType === 'passenger-breakdown' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <CustomToggle
                            label="Include All Bookings"
                            checked={includeNoShows}
                            onChange={(e) => dispatch(setIncludeNoShows(e.target.checked))}
                            variant="primary"
                            size="default"
                        />
                        <CustomToggle
                            label="Show Assistance"
                            checked={includeAssistanceBreakdown}
                            onChange={(e) => dispatch(setIncludeAssistanceBreakdown(e.target.checked))}
                            variant="primary"
                            size="default"
                        />
                    </div>
                    {datePickerNode}
                </div>
            )}

        </div>
    );
};

export default TimelineReportDashboard;
